import React, { useState } from 'react';
import { BlockListItem } from './BlockListItem';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { ADMIN_GROUPS } from '../../views/login/Login';
import { SelectPicker } from 'rsuite';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { useEligibilityContext } from '../../store/eligibility/eligibility-context';
import { IFingerprintListProps } from '../../store/eligibility/eligibility-interfaces';
import { getFormattedDate } from '../../utils/utilities';
import { setBlockByFingerPrints } from '../../store/eligibility/actions/order-block';

const blockDaysArr = [
	{
		label: '1 day',
		value: '1',
	},
	{
		label: '7 days',
		value: '7',
	},
	{
		label: '30 days',
		value: '30',
	},
	{
		label: '180 days',
		value: '180',
	},
	{
		label: '365 days',
		value: '365',
	},
	{
		label: 'Do not remove',
		value: 'notRemove',
	},
];

export interface IBlockBodyProps {
	fingerprintTypesToBlock: string[],
	blockUntil: string | Date | null | number,
	blockReason: string | null,
	networkLevel?: boolean,
}

interface Props {
	closeModal: () => void;
	orderId: string;
}

export const BlockListModal = (props: Props): React.ReactElement => {
	const { closeModal, orderId } = props;
	const { eligibilityState: { fingerprintsList, isSetBlockLoading }, dispatch } = useEligibilityContext();

	const [checkedItemsList, setCheckedItemsList] = useState<IFingerprintListProps[]>([]);
	const [isNetworkChecked, setIsNetworkChecked] = useState<boolean>(false);
	const [blockDays, setBlockDays] = useState<string>('notRemove');
	const [reason, setReason] = useState<string>('');
	const userData: any = useSelector<RootState>(({ auth }) => auth.userData, shallowEqual);
	const isSuperAdmin = userData?.userGroups?.some(el => el === ADMIN_GROUPS.SUPER_ADMIN);

	const findDateAfterDays = (days: number): string | number | Date => {
		const today = new Date();
		const millisecondsInADay = 24 * 60 * 60 * 1000;
		const targetDate = new Date(today.getTime() + days * millisecondsInADay);

		return getFormattedDate(targetDate, 'yyyy-MM-dd HH:mm:ss');
	};

	const handleConfirmBlock = async (): Promise<void> => {
		const fingerprintTypesToBlock = checkedItemsList.map(el => el.type);
		const blockUntil = blockDays !== 'notRemove' ? findDateAfterDays(Number(blockDays)) : null;
		const body: IBlockBodyProps = {
			fingerprintTypesToBlock,
			blockUntil,
			blockReason: reason,
		};
		if (isSuperAdmin) {
			body.networkLevel = isNetworkChecked;
		}
		await setBlockByFingerPrints(dispatch, orderId, body);
		closeModal();
	};

	return (
		<div className="px-8 py-10">
			<div className="mb-10 fs-6">
				Placing a payment on the block list results in the automatic blocking of all subsequent charges sharing identical characteristics. It`s important to exercise this action only when you genuinely suspect the legitimacy of the customer in question.
			</div>
			<div className="fs-6 fw-bolder">
				Select the characteristics you wish to block:
			</div>
			{
				fingerprintsList.map((item: IFingerprintListProps, index): JSX.Element => (
					<BlockListItem
						key={`${index} + 1`}
						{...item}
						checkedItemsList={checkedItemsList}
						setCheckedItemsList={setCheckedItemsList}
					/>
				))
			}

			<div className="fs-6 mt-10 mb-5 fw-bolder">Remove from list after:</div>
			<div className="merchants-selector main-merchant-filter-selector blocklist-selector mb-10">
				<SelectPicker
					data={blockDaysArr}
					appearance="subtle"
					placeholder='Do not remove'
					onSelect={setBlockDays}
					cleanable={false}
					searchable={false}
					defaultValue='notRemove'
					placement="bottomStart"

				/>
			</div>
			<div className="fs-6 fw-bolder mb-5">Please, write the reason for placing a payment on the block list:</div>
			<textarea
				className="form-control form-control-sm form-control-solid mt-1 h-100px resize-none mb-10"
				placeholder="Write reason here"
				maxLength={250}
				onChange={(e): void => setReason(e.target.value)}
			/>
			{
				isSuperAdmin && (
					<div className="d-flex align-items-center">
						<div className='form-check form-check-sm form-check-custom form-check-solid'>
							<input
								className='form-check-input'
								type='checkbox'
								checked={isNetworkChecked}
								onChange={(e): void => setIsNetworkChecked(e.target.checked)}
							/>
						</div>
						<div className="ms-2 fs-6 fw-bolder">Add to blocklist on a network level</div>
					</div>
				)
			}

			<div className="d-flex align-items-center justify-content-center mt-10">
				<ButtonComponent
					buttonName='Cancel'
					additionalClass="btn-sm me-20"
					buttonType={BUTTON_TYPE.CANCELLED}
					onButtonClick={closeModal}
				/>
				<ButtonComponent
					buttonName='Confirm'
					additionalClass="btn-sm w-100px"
					onButtonClick={handleConfirmBlock}
					loading={isSetBlockLoading}
					isDisabled={isSetBlockLoading}
				/>
			</div>



		</div>
	);
};
